import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { distinctUntilChanged, filter, map } from "rxjs/operators";
import { DialogComponent } from "./components/dialog/dialog.component";
import { Store } from "@ngrx/store";
import { PortalAppState } from "./store/reducer";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ClearErrorMessage, CurrentState, ErrorsState, LoginAction, SetDevSettinsAction } from 'common-lib';
import * as _ from "lodash";
import { ApplicationConstants } from "./app.constants";
import { register } from 'swiper/element/bundle';
import { BehaviorSubject, combineLatest } from "rxjs";
import { CheckSessionAction } from "./store/user/portal-user.actions";
import { devSettings } from "../environments/environment";
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

register()

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  private spinnerCreatedSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  public spinners: HTMLElement[] = [];

  constructor(
    protected router: Router,
    protected store: Store<PortalAppState>,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private currentState: CurrentState<PortalAppState>,
    private matIconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer
  ) {
    this.translateService.use(ApplicationConstants.defaultLanguage);
    this.store.dispatch(new SetDevSettinsAction(devSettings));
    this.initLoadingIndicator();
    this.setupErrorHandling();
    this.handleLogin();
    this.matIconRegistry.addSvgIcon(
      'skull',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/skull.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'open',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/open.svg')
    );
  }

  private initLoadingIndicator() {
    const loadingInProgressObs = this.store.select(state => state.uiState.requestsInProgress).pipe(map(requests => {
      let size = Object.keys(requests).length;
      return size > 0;
    }))
    const showFullscreenLoading = this.store.select(state => state.uiState.showLoading);

    const showLoading = combineLatest([
      showFullscreenLoading,
      loadingInProgressObs
    ]).pipe(distinctUntilChanged(_.isEqual), map(([showSpinner, loadingInProgress]) => {
      console.log('showFullscreenLoading status: ' + showSpinner);
      console.log('loadingInProgress: ' + loadingInProgress);
      return showSpinner || loadingInProgress;
    }));

    combineLatest([
      showLoading,
      this.spinnerCreatedSubject]).pipe(distinctUntilChanged(_.isEqual)).subscribe(async ([show, spinner]) => {
      console.log('Show spinner status: ' + show);
      console.log('Show spinner latest spinner: ' + spinner);
      if (show && !spinner) {
        console.log('Showing spinner');
        let loadingSpinner = this.createLoadingSpinner();
        this.spinners.push(loadingSpinner);
        this.spinnerCreatedSubject.next(loadingSpinner);
        console.log('spinners after added: ');
        console.log(this.spinners);
      } else if (!show) {
        if (this.spinners.length > 0) {
          while (this.spinners.length > 0) {
            console.log('Hiding spinner');
            this.removeSpinner(this.spinners.pop()!);
          }
        } else {
          console.error('No spinner found');
        }
        if (spinner) {
          this.removeSpinner(spinner);
        }
        this.spinnerCreatedSubject.next(null);
      }
    });
  }

  private createLoadingSpinner(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.classList.add('loading-overlay');
    document.body.appendChild(overlay);
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    overlay.style.zIndex = '9998';
    overlay.style.display = 'none';

    const spinner = document.createElement('div');
    spinner.classList.add('loading-spinner');
    spinner.innerText = 'Please wait...';
    document.body.appendChild(spinner);
    spinner.style.position = 'fixed';
    spinner.style.top = '50%';
    spinner.style.left = '50%';
    spinner.style.transform = 'translate(-50%, -50%)';
    spinner.style.zIndex = '9999';
    spinner.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    spinner.style.padding = '20px';
    spinner.style.color = '#fff';
    spinner.style.borderRadius = '10px';
    spinner.style.textAlign = 'center';
    spinner.style.display = 'none';

    overlay.style.display = 'block';
    spinner.style.display = 'block';
    return spinner;
  }

  private removeSpinner(spinner: HTMLElement): void {
    if (spinner && document.body.contains(spinner)) { // Check if spinner is still in the DOM
      spinner.style.display = 'none'; // Hide spinner
      document.body.removeChild(spinner);
      const overlay = document.querySelector('.loading-overlay') as HTMLElement;
      if (overlay) {
        overlay.style.display = 'none';
        document.body.removeChild(overlay);
      }
    } else {
      console.warn('Spinner is not in the DOM or already removed');
    }
  }

  private setupErrorHandling() {
    this.store.select(state => state.errors).pipe(
      distinctUntilChanged(_.isEqual),
      filter(error => !!error.key)).subscribe((error: ErrorsState) => {
      let title = this.translateService.instant('ERROR.DIALOG_TITLE');
      if (!!error.messageTitle) {
        title = this.translateService.instant(error.messageTitle);
      }
      let errorMessage;
      if (error.translatedMessage) {
        errorMessage = error.translatedMessage;
      } else {
        if (error.key) {
          let msg = this.translateService.instant(error.key.toString());
          if (msg == error.key.toString()) {
            if (error.defaultMessage) {
              msg = error.defaultMessage;
            } else {
              msg = this.translateService.instant('ERROR.GENERIC_ERROR');
            }
          }
          if (msg) {
            errorMessage = msg;
          }
        } else if (error.defaultMessage) {
          errorMessage = error.defaultMessage;
        } else if (error.statusCode == 500) {
          let msg = this.translateService.instant('ERROR.GENERIC_INTERNAL_BE_ERROR');
          errorMessage = msg;
        } else {
          let msg = this.translateService.instant('ERROR.GENERIC_ERROR');
          errorMessage = msg;
        }
      }

      if (error.additionalInfo && this.currentState.snapshot.dev.showErrorCause) {
        errorMessage += '\nAdditionalInfo: ' + error.additionalInfo;
      }

      if (error.rootCause && this.currentState.snapshot.dev.showErrorCause) {
        errorMessage += '\nCause: ' + error.rootCause;
      }

      this.dialog.closeAll();

      const dialogRef = this.dialog.open(DialogComponent, {
        panelClass: "error-dialog",
        height: '300px',
        width: '300px',
        data: {
          header: title,
          content: errorMessage,
          primaryBtnLabel: this.translateService.instant('BUTTON.CLOSE')
        },
      });
      dialogRef.afterClosed().toPromise().then(result => {
        this.store.dispatch(new ClearErrorMessage());
      })
    })
  }

  private handleLogin() {
    const loggedInObs = this.store.select(state => state.user.isLoggedIn);
    loggedInObs.pipe(filter(l => !l), distinctUntilChanged()).subscribe(loggedIn => {
      console.log('Logged in obs fired: ' + loggedIn);
      if (this.currentState.snapshot.dev.autoLogin) {
        let loginData = {
          userName: this.currentState.snapshot.dev.autoLoginUsername,
          password: this.currentState.snapshot.dev.autoLoginPassword,
          userId: null, autoLogin: false
        }
        let loginAction = new LoginAction({...loginData, autoLogin: true});
        this.store.dispatch(loginAction)
      }
      this.store.dispatch(new CheckSessionAction());
    })
  }

}
